import React, { useState, useEffect } from "react";
import { Typography, Container, Box, Grid } from "@mui/material";
import api from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { useTranslation } from "react-i18next";
import LoaderContainer from "../../components/LoaderContainer";
import Calendar from "../../assets/Icon/Calendar.png";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../../components/Alert";
import CustomProgramCard from "../../components/Custom/CustomProgramCard";
import CustomTabs from "../../components/Custom/CustomTabs";

export default function ProgramList() {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [otherPrograms, setOtherPrograms] = useState([]);
  const [recommendedPrograms, setRecommendedPrograms] = useState([]);

  const tabTitles = [t("Recommended"), t("Others")];
  const tabColors = ["#F3FDF3", "#F3FDF3"];
  const tabBordersColor = ["1px solid #4CAF50", "1px solid #4CAF50"];
  const textColors = ["#4CAF50", "#4CAF50"];

  const getUserId = () => {
    const userStr = localStorage.getItem("auth");
    if (!userStr) return null;

    try {
      const userData = JSON.parse(userStr);
      return userData?.user.app_user?.id;
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  };

  const currentUserId = getUserId();

  const loadOtherPrograms = async () => {
    try {
      const response = await api.get("/programs/get-user-related-programs/", {
        params: {
          user_id: currentUserId,
          relation_type: "others",
        },
      });
      setOtherPrograms(response.data.programs);
    } catch (err) {
      console.error("Error loading other programs:", err);
      showAlert("error", "Failed to load other programs");
    }
  };

  const loadRecommendedPrograms = async () => {
    try {
      const response = await api.get("/programs/get-user-related-programs/", {
        params: {
          user_id: currentUserId,
          relation_type: "recommended",
        },
      });
      setRecommendedPrograms(response.data.programs);
    } catch (err) {
      console.error("Error loading recommended programs:", err);
      showAlert("error", "Failed to load recommended programs");
    }
  };

  useEffect(() => {
    const loadAllPrograms = async () => {
      setLoading(true);
      await Promise.all([loadRecommendedPrograms(), loadOtherPrograms()]);
      setLoading(false);
    };
    loadAllPrograms();
  }, []);

  // Add tab change handler
  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
  };

  const handleViewProgramDetails = (programId) => {
    navigate(`/program/${programId}`);
  };

  const renderProgramData = (program) => {
    const programDetails = program;
    const programInputs = programDetails.program_inputs?.[0] || {};
    return [
      {
        label: t("Program Name"),
        value: programDetails.name || t("N/A"),
        type: "text",
      },
      {
        label: t("Crops"),
        value: programInputs.crop || t("N/A"),
        type: "text",
      },
      {
        label: t("Geographies"),
        value: programInputs.state || t("N/A"),
        type: "text",
      },
      {
        label: t("Cost to farmer"),
        value: programInputs.farmer_cost
          ? `${programInputs.farmer_cost} Rs. per acre`
          : t("N/A"),
        type: "text",
      },
      {
        label: t("GHG"),
        value: programInputs.ghg ? `${programInputs.ghg} per acre` : t("N/A"),
        type: "text",
      },
      {
        label: t("Water"),
        value: programInputs.water
          ? `${programInputs.water} per acre`
          : t("N/A"),
        type: "text",
      },
      {
        label: t("Program end date"),
        value: programInputs.program_end_date || t("N/A"),
        type: "text",
      },
    ];
  };

  const EmptyState = ({ message }) => (
    <div
      style={{
        height: "85%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div
        style={{
          margin: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <img
          src={Calendar}
          alt="My Programs"
          style={{
            width: "127px",
            height: "127px",
            marginBottom: "10px",
          }}
        />
        <Typography variant="subtitle1" sx={{ mb: 2, textAlign: "center" }}>
          {message}
        </Typography>
      </div>
    </div>
  );

  return (
    <>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container
          maxWidth="xs"
          style={{ height: "91vh", display: "flex", flexDirection: "column" }}
        >
          <Grid container spacing={1} mt={1}>
            <Grid item xs={12}>
              <CustomTabs
                numTabs={tabTitles.length}
                tabTitles={tabTitles}
                tabColors={tabColors}
                tabBordersColor={tabBordersColor}
                textColors={textColors}
                onTabChange={handleTabChange}
                value={activeTab}
              />
            </Grid>
          </Grid>

          {/* Render programs based on active tab */}
          {activeTab === 0 ? (
            // Recommended Programs
            recommendedPrograms.length > 0 ? (
              <div
                style={{
                  overflowY: "auto",
                  height: "100%",
                  textAlign: "center",
                  maxHeight: "100%",
                  marginTop: "15px",
                }}
              >
                {recommendedPrograms.map((program) => (
                  <Box
                    key={program.id}
                    onClick={() => handleViewProgramDetails(program.id)}
                    sx={{ cursor: "pointer", mb: 2 }}
                  >
                    <CustomProgramCard
                      id={program.id}
                      data={renderProgramData(program)}
                      onView={() => handleViewProgramDetails(program.id)}
                    />
                  </Box>
                ))}
              </div>
            ) : (
              <EmptyState
                message={t(
                  "No recommended programs found. Explore Other programs."
                )}
              />
            )
          ) : // Other Programs
          otherPrograms.length > 0 ? (
            <div
              style={{
                overflowY: "auto",
                height: "100%",
                textAlign: "center",
                maxHeight: "100%",
                marginTop: "15px",
              }}
            >
              {otherPrograms.map((program) => (
                <Box
                  key={program.id}
                  onClick={() => handleViewProgramDetails(program.id)}
                  sx={{ cursor: "pointer", mb: 2 }}
                >
                  <CustomProgramCard
                    id={program.id}
                    data={renderProgramData(program)}
                    onView={() => handleViewProgramDetails(program.id)}
                  />
                </Box>
              ))}
            </div>
          ) : (
            <EmptyState
              message={t(
                "No other programs available. Explore Recommended programs."
              )}
            />
          )}
        </Container>
      </LoaderContainer>
    </>
  );
}
